/**
 * Tenant Admin → Helper Budgets (read-only view).
 *
 * Per EP-2 §9.3. Tenant admins (role='admin') see the resolved budgets
 * that apply to their tenant's helper-chat workloads. Read-only;
 * changes are sysadmin-only at /sysadmin/platform/helper-budgets.
 *
 * Primary use case: a tenant admin investigating a user-reported
 * timeout can see "your `architect` helper on `argus_pro` has a 30 s
 * first-token budget — that's what timed out". Saves a
 * "what's the timeout set to?" support ticket per spec §16 Q2.
 */

import { createFileRoute } from '@tanstack/react-router';
import { Timer, Info, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SectionCard } from '@/components/ui/section-card';
import { PageError } from '@/components/ui/page-error';
import {
  useTenantHelperBudgets,
  type ResolutionSource,
} from '@/lib/use-sysadmin-ep2';

export const Route = createFileRoute('/admin/helper-budgets')({
  component: TenantHelperBudgetsPage,
});

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(0)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

const SOURCE_LABEL: Record<ResolutionSource, string> = {
  db_explicit: 'override',
  db_inherited_brand: 'brand default',
  db_inherited_kind: 'kind default',
  db_factory: 'factory',
};

function chipClass(source: ResolutionSource): string {
  if (source === 'db_explicit') {
    return 'bg-[#1890FF]/10 text-[#1890FF] border-[#1890FF]/30';
  }
  if (source === 'db_factory') {
    return 'bg-amber-500/10 text-amber-700 border-amber-500/30';
  }
  return 'bg-muted text-muted-foreground';
}

function TenantHelperBudgetsPage() {
  const { data, isLoading, error, refetch } = useTenantHelperBudgets();
  if (error) return <PageError error={error} onRetry={refetch} />;

  const brands = data?.brands ?? [];
  const helperKinds = data?.helperKinds ?? [];
  const matrix = data?.matrix ?? [];

  const cellMap = new Map<string, (typeof matrix)[number]>();
  for (const c of matrix) cellMap.set(`${c.brandId}:${c.helperKind}`, c);

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Timer className="h-6 w-6 text-[#1890FF]" />
          Helper Budgets
        </h1>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Read-only view of the streaming budgets that govern your tenant's
          helper-chat workloads. Each helper-chat request resolves its
          first-token, stream-inactivity, total, and heartbeat values
          from this matrix. Changes are platform-wide; contact your
          sysadmin to request a budget adjustment.
        </p>
      </header>

      <div className="rounded-md border border-border bg-muted/40 p-3 flex items-start gap-2 text-sm">
        <Info className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
        <div className="text-muted-foreground">
          Budgets are platform-wide (sysadmin-managed). To change a value,
          contact your platform administrator with the brand, helper kind,
          and the budget tier you want adjusted.
        </div>
      </div>

      <SectionCard
        title="Budget matrix"
        description={
          isLoading
            ? 'Loading…'
            : `${brands.length} brand(s) × ${helperKinds.length} helper kind(s) = ${matrix.length} cells`
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                <th className="p-2">Brand</th>
                {helperKinds.map((k) => (
                  <th key={k} className="p-2 text-center">
                    {k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {brands.map((brand) => (
                <tr key={brand.id} className="border-b">
                  <td className="p-2 font-medium">{brand.slug}</td>
                  {helperKinds.map((kind) => {
                    const cell = cellMap.get(`${brand.id}:${kind}`);
                    if (!cell) {
                      return (
                        <td
                          key={kind}
                          className="p-2 text-center text-xs text-muted-foreground"
                        >
                          —
                        </td>
                      );
                    }
                    return (
                      <td key={kind} className="p-2">
                        <div className="rounded border border-border bg-card px-2 py-1.5">
                          <div className="text-xs font-medium">
                            {formatMs(cell.resolved.firstTokenBudgetMs)} ·{' '}
                            {formatMs(cell.resolved.streamInactivityBudgetMs)} ·{' '}
                            {formatMs(cell.resolved.totalBudgetMs)}
                          </div>
                          <Badge
                            variant="outline"
                            className={`mt-1 text-[10px] ${chipClass(cell.source)}`}
                          >
                            {SOURCE_LABEL[cell.source]}
                          </Badge>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {brands.length === 0 && !isLoading && (
                <tr>
                  <td
                    colSpan={helperKinds.length + 1}
                    className="p-4 text-center text-muted-foreground"
                  >
                    No brands assigned to this tenant.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-xs text-muted-foreground">
          Cell format: first-token · stream-inactivity · total.
        </div>
      </SectionCard>
    </div>
  );
}
